/* eslint-disable react-refresh/only-export-components -- kit compartilhado (hooks + componentes) de propósito, como o _shared */
/**
 * Camada compartilhada de gráficos — MOTION (rodada GRAFICOS-CONSISTENTES,
 * 2026-07-10). Regra do Matheus: TODO gráfico do Financeiro segue o MESMO
 * padrão (animação de entrada, hover/tooltip, count-up, reduced-motion) via
 * ESTA camada — nada de copiar-colar por aba.
 *
 * Comportamento vem dos mockups (mockup-fluxo-caixa-imersivo.html e
 * mockup-graficos-referencia.html); a APARÊNCIA usa a ID real do app
 * (CORES/SEMAFORO_COR/tokens do _shared) — se o mockup divergir, a ID vale.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Duração/das animações — ÚNICAS pra todo o Financeiro (não customizar por aba). */
export const MOTION = {
  drawMs: 1600, // linha se desenha
  growMs: 1100, // barras crescem / medidor preenche
  sweepMs: 1200, // donut varre
  countMs: 1300, // count-up dos números-chave
  riseMs: 500, // entrada dos painéis
  staggerMs: 90, // cascata entre painéis/cards
} as const;

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** true quando o usuário desligou animações no sistema → TUDO renderiza estático. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

/**
 * COUNT-UP dos números-chave (padrão (c)). Ease cubic, respeita
 * reduced-motion (pula direto pro alvo). Movido da Visão Geral pra cá.
 */
export function useCountUp(alvoCentavos: number): number {
  const reduced = useReducedMotion();
  const [v, setV] = useState(reduced ? alvoCentavos : 0);
  const raf = useRef(0);
  useEffect(() => {
    if (reduced) {
      setV(alvoCentavos);
      return;
    }
    const inicio = performance.now();
    cancelAnimationFrame(raf.current);
    const tick = (t: number) => {
      const p = Math.min(1, (t - inicio) / MOTION.countMs);
      setV(Math.round(alvoCentavos * easeOutCubic(p)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [alvoCentavos, reduced]);
  return v;
}

/**
 * Entrada em SEQUÊNCIA (padrão (a) pra painéis/cards): opacity+translateY com
 * cascata pelo `ordem`. Reduced-motion: aparece direto.
 */
export function Rise({ ordem = 0, className = '', children, ...rest }: {
  ordem?: number;
  className?: string;
  children: ReactNode;
} & Record<string, unknown>) {
  const reduced = useReducedMotion();
  const [inScreen, setIn] = useState(reduced);
  useEffect(() => {
    if (reduced) return;
    const t = setTimeout(() => setIn(true), MOTION.staggerMs * ordem);
    return () => clearTimeout(t);
  }, [ordem, reduced]);
  return (
    <div
      className={className}
      style={{
        opacity: inScreen ? 1 : 0,
        transform: inScreen ? 'none' : 'translateY(10px)',
        transition: reduced ? undefined : `opacity ${MOTION.riseMs}ms ease, transform ${MOTION.riseMs}ms ease`,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface TipState {
  /** posição em % do container (deixa o tooltip responsivo). */
  xPct: number;
  yPct: number;
  titulo: string;
  valor: string;
  valorCor?: string;
  extra?: string;
}

/**
 * TOOLTIP único do padrão (b) — mesma cara em todo gráfico: caixa escura,
 * título uppercase, valor tabular, linha de contexto.
 *
 * Fix A3 (ajustes 2026-07-10, NÃO REVERTER): CLAMP na viewport — depois de
 * renderizar, mede o retângulo real e desloca horizontalmente o que estourar
 * a borda da janela (o bug do "Déficit final total": o tooltip do sparkline
 * do ÚLTIMO card saía cortado pra fora da tela). Absoluto — nunca empurra o
 * layout do card.
 */
export function TooltipGrafico({
  tip,
  clampRef,
}: {
  tip: TipState | null;
  /**
   * PARTE2-TETO G4: quando presente, o clamp usa os limites DESTE container
   * (o box do gráfico) em vez da viewport — o tooltip nunca vaza do gráfico
   * nem empurra a página. Sparklines seguem sem clampRef (o tooltip é maior
   * que o card mini e PRECISA vazar — clamp só na viewport, fix A3).
   */
  clampRef?: { current: HTMLElement | null };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shiftPx, setShiftPx] = useState(0);
  useLayoutEffect(() => {
    setShiftPx(0);
    if (!tip || !ref.current) return;
    // mede APÓS zerar o shift (raf garante o layout com shift 0 aplicado)
    const raf = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margem = 4;
      const box = clampRef?.current?.getBoundingClientRect();
      const limEsq = box ? box.left + margem : margem;
      const limDir = box ? box.right - margem : window.innerWidth - margem;
      if (r.right > limDir) {
        setShiftPx(limDir - r.right);
      } else if (r.left < limEsq) {
        setShiftPx(limEsq - r.left);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [tip, clampRef]);
  return (
    <div
      ref={ref}
      data-testid="grafico-tooltip"
      className="absolute pointer-events-none rounded-lg px-2.5 py-1.5 text-xs text-white bg-ink-1 dark:bg-black/85 shadow-lg z-10 whitespace-nowrap transition-opacity duration-100"
      style={{
        left: `${tip?.xPct ?? 0}%`,
        top: `${tip?.yPct ?? 0}%`,
        transform: `translate(calc(-50% + ${shiftPx}px),-120%)`,
        opacity: tip ? 1 : 0,
      }}
    >
      {tip && (
        <>
          <span className="block text-xs uppercase tracking-wide text-white/60">{tip.titulo}</span>
          <span className="font-bold tabular-nums" style={tip.valorCor ? { color: tip.valorCor } : undefined}>
            {tip.valor}
          </span>
          {tip.extra && <span className="block text-xs text-white/60 mt-0.5">{tip.extra}</span>}
        </>
      )}
    </div>
  );
}
